import { useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";

const JITSI_HOST = "meet.jit.si";

const MeetingRoom = () => {
  const { roomName } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const apiRef = useRef(null);

  useEffect(() => {
    // Load Jitsi Meet External API script and mount the meeting
    let cancelled = false;
    const mount = () => {
      if (cancelled || !window.JitsiMeetExternalAPI || !containerRef.current) return;
      containerRef.current.innerHTML = "";
      apiRef.current = new window.JitsiMeetExternalAPI(JITSI_HOST, {
        roomName,
        parentNode: containerRef.current,
        width: "100%",
        height: "100%",
        userInfo: {
          displayName: user?.name || "Guest",
          email: user?.email || "",
        },
        configOverwrite: {
          prejoinPageEnabled: true,
          disableDeepLinking: true,
          startWithAudioMuted: true,
          startWithVideoMuted: false,
        },
        interfaceConfigOverwrite: {
          MOBILE_APP_PROMO: false,
          SHOW_JITSI_WATERMARK: false,
          SHOW_WATERMARK_FOR_GUESTS: false,
          DEFAULT_BACKGROUND: "#E8F5E9",
          TOOLBAR_BUTTONS: [
            "microphone","camera","closedcaptions","desktop","fullscreen",
            "fodeviceselection","hangup","chat","raisehand","videoquality",
            "filmstrip","tileview","select-background","invite","toggle-camera",
          ],
        },
      });
      apiRef.current.on("readyToClose", () => navigate("/app/meet"));
    };

    const existing = document.getElementById("jitsi-api");
    if (existing) mount();
    else {
      const s = document.createElement("script");
      s.id = "jitsi-api";
      s.src = `https://${JITSI_HOST}/external_api.js`;
      s.async = true;
      s.onload = mount;
      document.body.appendChild(s);
    }

    return () => {
      cancelled = true;
      try { apiRef.current?.dispose(); } catch {}
    };
  }, [roomName, user, navigate]);

  const copyLink = () => {
    const url = `${window.location.origin}/app/meet/room/${roomName}`;
    navigator.clipboard.writeText(url);
    toast.success("LINK COPIED");
  };

  return (
    <div className="h-full flex flex-col">
      <header className="border-b border-line p-3 flex items-center gap-3 bg-paper-white">
        <button onClick={() => navigate("/app/meet")} className="p-2 border border-line" data-testid="meet-back">
          <ArrowLeft size={16} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="label-tag text-ink-3">// LIVE ROOM</div>
          <div className="font-heading font-bold truncate">{roomName}</div>
        </div>
        <button onClick={copyLink} className="btn-secondary flex items-center gap-2" data-testid="meet-copy-link">
          <Copy size={14} /> COPY LINK
        </button>
      </header>
      <div className="flex-1 min-h-0 bg-ink" ref={containerRef} data-testid="jitsi-container" />
    </div>
  );
};

export default MeetingRoom;
